import React, { useState } from "react";
import {
  Card,
  CardContent,
  Box,
  Typography,
  IconButton,
  Button,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import tz from "dayjs/plugin/timezone";
import { Messages } from "./types";
import { NetworkManager } from "../../../Components/NetworkManager";
import { CommonUtil } from "../../../utils/CommonUtil";
import ToastManager from "../../../Components/ToastManager";
import StopMessageConfirmDialog from "./StopMessageConfirmDialog";

dayjs.extend(utc);
dayjs.extend(tz);

const stopButtonSx = {
  backgroundColor: "#fd6c33",
  color: "#fff",
  textTransform: "none",
  padding: "2px 10px",
  fontWeight: 500,
  "&:hover": { backgroundColor: "#fd6c33" },
  "&.Mui-disabled": { backgroundColor: "#1DA1F2", color: "#fff" },
};

const stopButtonActiveSx = {
  backgroundColor: "#fd6c33",
  color: "#fff",
  textTransform: "none",
  padding: "2px 10px",
  fontWeight: 500,
  "&:hover": { backgroundColor: "#fd6c33" },
  "&.Mui-disabled": { backgroundColor: "#bdbdbd", color: "#fff" },
};

interface Props {
  message: Messages;
  onEdit?: (message: Messages) => void;
}

const MessageCard: React.FC<Props> = ({ message, onEdit }) => {
  const [isStopped, setIsStopped] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [stopping, setStopping] = useState(false);
  const [stopError, setStopError] = useState<string | null>(null);

  const handleStopConfirm = () => {
    setStopping(true);
    setStopError(null);
    NetworkManager.sendJsonRequest(
      "broadcastMessageTemplates/cancelMessageTemplate",
      "",
      `messageTemplateId=${message.messageTemplateId}`,
      "POST",
      false,
      (data: any) => {
        setStopping(false);
        const respData = CommonUtil.parseData(data);
        if (respData?.statusCode === "BM0000") {
          ToastManager.showToast("Message Cancelled Successfully", "Success");
          setIsStopped(true);
          setDialogOpen(false);
        } else {
          const errMsg =
            respData?.Message ?? respData?.["Status Code Message"] ?? "Failed to cancel the message. Please try again.";
          setStopError(errMsg);
          ToastManager.showToast(errMsg, "Error");
        }
      },
      (error: any) => {
        setStopping(false);
        const errMsg =
          error?.message ?? "A network error occurred. Please try again.";
        setStopError(errMsg);
        ToastManager.showToast(errMsg, "Error");
      },
    );
  };

  return (
    <>
      <Card
        variant="outlined"
        sx={{
          borderRadius: 1,
          mb: 1,
          boxShadow: "0px 2px 6px rgba(0,0,0,0.05)",
          "&:hover": { boxShadow: "0px 4px 10px rgba(0,0,0,0.1)" },
          p: 1.5,
        }}>
        <CardContent sx={{ p: 0, pb: "0px !important" }}>
          <Box sx={{ display: "flex", justifyContent: "space-between" }}>
            <Box>
              <Typography
                sx={{ fontSize: 18, fontWeight: 600, textAlign: "start" }}>
                {message.messageTemplateName}
              </Typography>
              <Typography
                sx={{ fontSize: 16, fontWeight: 400, textAlign: "start" }}>
                {message.headline}
              </Typography>
              <Typography
                sx={{ fontSize: 14, fontWeight: 400, textAlign: "start" }}>
                Scheduled time:{" "}
                {message.scheduledTime
                  ? dayjs(message.scheduledTime)
                      .tz("America/New_York")
                      .format("YYYY-MM-DD hh:mm:ss A")
                  : "—"}
              </Typography>
            </Box>

            <Box sx={{ display: "flex", alignItems: "center", gap: 3 }}>
              {onEdit && (
                <>
                  <Button
                    variant="contained"
                    onClick={() => {
                      setStopError(null);
                      setDialogOpen(true);
                    }}
                    disabled={isStopped}
                    sx={isStopped ? stopButtonActiveSx : stopButtonSx}>
                    {isStopped ? "Stopped" : "Stop"}
                  </Button>
                  <IconButton
                    onClick={() => onEdit(message)}
                    aria-label="edit"
                    disabled={isStopped}>
                    <EditIcon fontSize="medium" />
                  </IconButton>
                </>
              )}
            </Box>
          </Box>
        </CardContent>
      </Card>

      <StopMessageConfirmDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        onConfirm={handleStopConfirm}
        loading={stopping}
        error={stopError}
      />
    </>
  );
};

export default MessageCard;
